import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage } from '../../util/reactIntl';
import { isAdminUser } from '../../util/userHelpers';
import { generateLinkProps } from '../../util/routes';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';

import { NamedLink, ExternalLink } from '../../components';
import EditIcon from './EditIcon';

import css from './ListingPage.module.css';

const STATE_TO_VALUE_ID = {
  [LISTING_STATE_DRAFT]: 'EditListingPublishPanel.stateDraft',
  [LISTING_STATE_PENDING_APPROVAL]: 'EditListingPublishPanel.statePendingApproval',
  [LISTING_STATE_PUBLISHED]: 'EditListingPublishPanel.statePublished',
  [LISTING_STATE_CLOSED]: 'EditListingPublishPanel.stateClosed',
};

const STATE_TO_EXPLANATION_ID = {
  [LISTING_STATE_DRAFT]: 'EditListingPublishPanel.stateDraftExplanation',
  [LISTING_STATE_PENDING_APPROVAL]: 'EditListingPublishPanel.statePendingApprovalExplanation',
  [LISTING_STATE_PUBLISHED]: 'EditListingPublishPanel.statePublishedExplanation',
  [LISTING_STATE_CLOSED]: 'EditListingPublishPanel.stateClosedExplanation',
};

const CTAButtonMaybe = props => {
  const { data, routeConfiguration, userId, userEmail, listingId, isPendingApproval } = props;

  const hasValidType = data?.type && data.type !== 'none';
  const isCTAEnabled = hasValidType && isPendingApproval;

  if (!isCTAEnabled) return null;

  const { type, text, href } = data;
  const ctaLink = generateLinkProps(type, href, routeConfiguration, userId, userEmail, listingId);
  const isInternalLink = type === 'internal' && ctaLink.route;

  return isInternalLink ? (
    <NamedLink
      name={ctaLink.route.name}
      to={ctaLink.route.to}
      params={ctaLink.route.params}
      className={css.actionBarCtaButton}
    >
      {text}
    </NamedLink>
  ) : (
    <ExternalLink href={ctaLink.link} className={css.actionBarCtaButton}>
      {text}
    </ExternalLink>
  );
};

/**
 * ActionBarMaybe: card with listing state, explanation, and actions.
 * Same vibe as EditListingPublishPanel (white card, state label, explanation, Preview + Edit/CTA).
 */
export const ActionBarMaybe = props => {
  const {
    rootClassName,
    className,
    isOwnListing,
    listing,
    currentUser,
    editParams,
    showNoPayoutDetailsSet,
  } = props;

  const state = listing?.attributes?.state;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;

  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const approvalToPublishOptions =
    config?.accessControl?.listings?.requireApprovalToPublishOptions?.callToAction || {};

  const cardClasses = classNames(rootClassName || css.actionBarCard, className);

  // Payout details missing (own listing)
  if (isOwnListing && showNoPayoutDetailsSet) {
    return (
      <div className={cardClasses} aria-label="Listing status">
        <div className={css.actionBarSectionRow}>
          <div className={css.actionBarLeftContent}>
            <p className={css.actionBarStateValue}>
              <FormattedMessage id="ListingPage.addPayoutDetails" />
            </p>
            <p className={css.actionBarStateExplanation}>
              <FormattedMessage id="ListingPage.addPayoutDetailsMessage" />
            </p>
            <div className={css.actionBarActions}>
              <NamedLink name="StripePayoutPage" className={css.actionBarPayoutLink}>
                <EditIcon className={css.editIcon} />
                <FormattedMessage id="ListingPage.addPayoutDetails" />
              </NamedLink>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Own listing: draft / pending / published / closed
  if (isOwnListing && STATE_TO_VALUE_ID[state]) {
    const hasValidType = approvalToPublishOptions?.type && approvalToPublishOptions.type !== 'none';
    const isCTAEnabled = hasValidType && isPendingApproval;
    const messageId = isDraft ? 'ListingPage.finishListing' : 'ListingPage.editListing';

    return (
      <div className={cardClasses} aria-label="Listing status">
        <div className={css.actionBarSectionRow}>
          <div className={css.actionBarLeftContent}>
            <p className={css.actionBarStateValue}>
              <FormattedMessage id={STATE_TO_VALUE_ID[state]} />
            </p>
            <p className={css.actionBarStateExplanation}>
              <FormattedMessage id={STATE_TO_EXPLANATION_ID[state]} />
            </p>
            <div className={css.actionBarActions}>
              <NamedLink
                name="EditListingPage"
                params={editParams}
                className={css.actionBarEditLink}
              >
                <EditIcon className={css.editIcon} />
                <FormattedMessage id={messageId} />
              </NamedLink>
              <CTAButtonMaybe
                data={approvalToPublishOptions}
                routeConfiguration={routeConfiguration}
                listingId={listing?.id?.uuid}
                userId={currentUser?.id?.uuid}
                userEmail={currentUser?.attributes?.email}
                isPendingApproval={isPendingApproval}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admins viewing someone else's listing: state + link to the admin listing editor
  if (!isOwnListing && isAdminUser(currentUser) && STATE_TO_VALUE_ID[state] && listing?.id) {
    return (
      <div className={cardClasses} aria-label="Listing status">
        <div className={css.actionBarSectionRow}>
          <div className={css.actionBarLeftContent}>
            <p className={css.actionBarStateValue}>
              <FormattedMessage id={STATE_TO_VALUE_ID[state]} />
            </p>
            <p className={css.actionBarStateExplanation}>
              <FormattedMessage id="ListingPage.adminActionBarExplanation" />
            </p>
            <div className={css.actionBarActions}>
              <NamedLink
                name="AdminEditListingPage"
                params={{ id: listing.id.uuid, tab: 'details' }}
                className={css.actionBarEditLink}
              >
                <EditIcon className={css.editIcon} />
                <FormattedMessage id="ListingPage.adminEditListing" />
              </NamedLink>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Customers (not own listing): do not show the action bar at all
  if (!isOwnListing) {
    return null;
  }

  return null;
};

export default ActionBarMaybe;
